<template>
  <div id="app">
    <section role="region" aria-label="Search Designer" id="main-container">
      <div class="main-wrapper">
          <section id="preview" aria-label="Preview">
                <h2 id="playground-lg">{{texts.home.configurationAndPlayground}}</h2>
                <h2 id="playground-sm">{{texts.home.interactivePlayground}}</h2>
            <Tabs nav-id="preview-groups" label="Switch Preview" :tabs="tabs" :change="updateTab" :active-tab="activeTab"  :style="{marginBottom: '2em'}"/>
            <DesktopPreview v-if="wasConfigLoaded" :config="ss360Config" :style="{display: activeTab===0?'': 'none'}" :demo="demo"  :is-visible="activeTab===0" />
            <MobilePreview v-if="wasConfigLoaded" :config="ss360Config"  :style="{display: activeTab===1?'': 'none'}" :demo="demo" :is-visible="activeTab===1"/>
          </section>
          <div class="config-wrapper">
              <h2 id="configuration">{{texts.home.configuration}}</h2>
              <div class="h2-like-separator"></div>
              <Presets :config="ss360Config" class="active" :after-change="logPresetChosen" :on-restore="restoreDefaults" :on-undo="popState"/>
              <Configuration :config="ss360Config" aria-labelledby="configuration"/>
          </div> 
      </div>
      <div v-if="!hasFinishedConfiguring" class="installation-toggle-wrapper">
        <button class="installation-toggle cta" @click="toggleInstallation">{{texts.home.configurationToggle}}</button>
      </div>
      <div class="separator" role="presentation"></div>
      <h2 v-if="hasFinishedConfiguring" id="installation">{{texts.home.installationInstructions}}</h2>
      <InstallationInstructions v-if="hasFinishedConfiguring" :config="ss360Config" :siteId="siteId" :on-signup="logSignup" :source="source"/>
    </section>
  </div>
</template>

<script>
import Configuration from "./components/Configuration";
import DesktopPreview from "./components/preview/DesktopPreview";
import MobilePreview from "./components/preview/MobilePreview";
import Tabs from "./components/Tabs";
import InstallationInstructions from "./components/InstallationInstructions";
import Presets from "./components/configuration/Presets";
import SearchDesignerIntroduction from "./components/SearchDesignerIntroduction";
import axios from "axios";
import qs from "qs";
import KeenTracking from "keen-tracking";
import ConfigHelper from './utils/ConfigHelper';
import Vue from 'vue';
import Texts from './localization/Texts';
import VueGlobalVar from 'vue-global-var';

let texts = Texts.EN;
if(window.location.href.indexOf('/de/') !== -1) {
	texts = Texts.DE;
} else if (window.location.href.indexOf('/fr/') !== -1) {
	texts = Texts.FR;
}

Vue.use(VueGlobalVar, {
  globals: {
    texts
  }
});

const SS360CONFIG_API = "//api.sitesearch360.com/users/ss360Config";

const defaults = require('./assets/DefaultConfig.js');
const defaultsString = JSON.stringify(defaults);

const KeenClient = new KeenTracking({
  projectId: "5b106943c9e77c00019fa493",
  writeKey:
    "889C04742EA75194C46ADE0FB10F4EA4537CA7829F2EAAA88B067A2AF04F7DE44CF88EAB86F0DF72CAA6D0C211F5312922B8D75F79215E1ECA759494B934D8B65153F74D4836427CD5649845C4B9E519B59EABDDCB48C3D703C41247E9D793F6"
});
const KeenHelpers = KeenTracking.helpers;
const KeenUtils = KeenTracking.utils;

export default {
  name: "App",
  components: {
    Configuration,
    DesktopPreview,
    MobilePreview,
    Tabs,
    InstallationInstructions,
    Presets,
    SearchDesignerIntroduction
  },
  props: {
    demo: String
  },
  data() {
    return {
      activeTab: 0,
      tabs: ["Desktop", "Mobile"],
      siteId: undefined,
      ss360Config: defaults,
      ss360ConfigUpdateTimeout: -1,
      ss360ConfigUpdateLogTimeout: -1,
      configChangeCount: 0,
      hasPlaygroundBeenTracked: false,
      hasFinishedConfiguring: false,
      shouldTrack: !('localStorage' in window) || window.localStorage.getItem("ss360DisableTracking")!="true",
      wasConfigLoaded: false,
      source: undefined
    };
  },
  methods: {
    updateTab(idx) {
      this.activeTab = idx;
    },
    updateGlobalConfig() {
      //TODO: remove defaults before saving
      let configCopy = JSON.parse(JSON.stringify(this.ss360Config));
      configCopy['siteId'] = this.siteId;
      configCopy = ConfigHelper.removeDefaults(configCopy, defaults);
      const data = qs.stringify({
        siteId: this.siteId,
        ss360Config: JSON.stringify(configCopy)
      });

      const headers = {
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8"
      };
      axios.put(SS360CONFIG_API, data, headers);
    },
    assertConfig(){
      Object.keys(defaults).forEach((key)=>{
          if(!(key in this.ss360Config)){
            try{
              Vue.set(this.ss360Config, key, JSON.parse(JSON.stringify(defaults[key])));
            }catch(ex){

            }
          }else if(this.ss360Config[key] instanceof Object){
            Object.keys(defaults[key]).forEach((innerKey)=>{
              this.assertObject(this.ss360Config[key], defaults[key], innerKey)
            });
          }
      });
    },
    assertObject(configObj, defaultObj, key){
      if(defaultObj[key]===undefined){
        return;
      }
      if(configObj[key]===undefined){
        if(defaultObj[key] instanceof Object){
          Vue.set(configObj, key, JSON.parse(JSON.stringify(defaultObj[key])));
        }else {
          Vue.set(configObj, key, defaultObj[key]);
        }
      }else if((defaultObj[key] instanceof Object) && !(defaultObj[key] instanceof Array)){
        Object.keys(defaultObj[key]).forEach((innerKey)=>{
          this.assertObject(configObj[key], defaultObj[key], innerKey);
        });
      }
    },
    logPresetChosen(type) {
      if(!this.shouldTrack){
        return;
      }
      KeenClient.recordEvent("presets", {
        type: type
      });
    },
    logSignup() {
      if(!this.shouldTrack){
        return;
      }
      KeenClient.recordEvent("signup", {});
    },
    restoreDefaults() {
      if (window.confirm("Revert all changes?")) {
        this.ss360Config = JSON.parse(defaultsString);
      }
    },
    updateHistory(){
        const history = JSON.parse(window.localStorage.getItem("ss360Config-history")) || [];
        if(history.length > 0 && JSON.stringify(this.ss360Config)==JSON.stringify(history[history.length-1])){//check whether state was restored
        }else{
          history.push(this.ss360Config);
          if(history.length > 10){
            history.splice(0, history.length - 10);
          }
          window.localStorage.setItem("ss360Config-history", JSON.stringify(history));
        }
    },
    popState(){
      const history = JSON.parse(window.localStorage.getItem("ss360Config-history")) || [];
      if(history.length>=2){
        const newState = history[history.length - 2];
        history.splice(history.length - 2, 2);
        this.ss360Config = newState;
      }else{
        history.splice(0, history.length);
      }
      window.localStorage.setItem("ss360Config-history", JSON.stringify(history));      
    },
    toggleInstallation(){
        this.hasFinishedConfiguring = true;
        window.localStorage.setItem("ss360-show-installation", true);
       if(!this.shouldTrack){
        return;
      }
      KeenClient.recordEvent("show-installation", {});
    }
  },
  watch: {
    ss360Config: {
      deep: true,
      handler(c) {
		// FIXME: sometimes fullscreen turns on automatically
        if ("localStorage" in window) {
          window.localStorage.setItem(
            "ss360Config",
            JSON.stringify(this.ss360Config)
          );
          clearTimeout(this.historyTimeoutId);
          this.historyTimeoutId = setTimeout(this.updateHistory.bind(this), 500);
          if (this.siteId !== undefined) {
            clearTimeout(this.ss360ConfigUpdateTimeout);
            this.ss360ConfigUpdateTimeout = setTimeout(
              this.updateGlobalConfig.bind(this),
              750
            );
          }
        }
        clearTimeout(this.ss360ConfigUpdateLogTimeout);
        this.ss360ConfigUpdateLogTimeout = setTimeout(() => {
          
          if(this.configChangeCount===0){
            this.configChangeCount++;
            return;
          }
            this.configChangeCount++;
           if(!this.shouldTrack){
              return;
            }
          //timeout because of colorpicker etc.
            KeenClient.recordEvent("config-change-v2", {
            });
        }, 300);
      }
    }
  },
  mounted() {
    // get session id
    var searchParams = window.location.search
      .replace("?", "")
      .split("&")
      .reduce((acc, el) => {
        acc[el.split("=")[0]] = el.split("=")[1];
        return acc;
      }, {});
    if (searchParams.siteId !== undefined) {
      this.siteId = searchParams.siteId;
    }
    if(searchParams.source !== undefined || (('localStorage' in window) && window.localStorage.getItem("source") !== null)) {
      this.source = searchParams.source || window.localStorage.getItem("source");
      if('localStorage' in window) {
        window.localStorage.setItem('source', this.source);
      }
    }
    // load from localStorage
    if ("localStorage" in window && window.localStorage.getItem("ss360Config") != null && window.localStorage.getItem("ss360Config") != undefined) {
      try {
        this.ss360Config = JSON.parse(window.localStorage.getItem("ss360Config"));
      } catch (e) {
        console.warn(e);
      }
    } else if (searchParams.ecom === '1') {
		this.ss360Config = JSON.parse(JSON.stringify(this.ss360Config));
		this.ss360Config.filters.enabled = true;
		this.ss360Config.filters.position = 'left';
	}

    this.assertConfig();

    // compatibility
    if(!('filters' in this.ss360Config)){
      this.ss360Config.filters = JSON.parse(JSON.stringify(defaults.filters));
    }
    if(!('tracking' in this.ss360Config)){
      this.ss360Config.tracking = JSON.parse(JSON.stringify(defaults.tracking));
    }
    if(!('allowCookies' in this.ss360Config)){
      this.ss360Config.allowCookies = defaults.allowCookies;
    }
    if(!('voiceSearch' in this.ss360Config)){
      this.ss360Config.voiceSearch = JSON.parse(JSON.stringify(defaults.voiceSearch));
    }
    if(!('mobileScrollOnFocus' in this.ss360Config.suggestions)){
      this.ss360Config.suggestions.mobileScrollOnFocus = defaults.suggestions.mobileScrollOnFocus;
    }
    if(!('forceTabs' in this.ss360Config.layout.navigation)){
      this.ss360Config.layout.navigation.forceTabs = defaults.layout.navigation.forceTabs;
    }

    this.wasConfigLoaded = true;
    
    if('localStorage' in window){
      this.hasFinishedConfiguring = window.localStorage.getItem("ss360-show-installation") ? true : false;
    }else{
      this.hasFinishedConfiguring = true;
    }
    // load from api
    if (this.siteId !== undefined) {
      axios.get(SS360CONFIG_API + "?siteId=" + this.siteId).then(response => {
        const data = response.data;
        if (data && data.ss360Config) {
            this.ss360Config = JSON.parse(data.ss360Config);
            this.assertConfig();
        }
      });
    }
    // keen tracking
    if(this.shouldTrack){
      this.sessionCookie = KeenUtils.cookie("ss360-viscon");
      if (!this.sessionCookie.get("guest_id")) {
        this.sessionCookie.set("guest_id", KeenHelpers.getUniqueId());
      }
      this.sessionCookie.set("session_id", KeenHelpers.getUniqueId());
      KeenClient.extendEvents(() => {
        return {
          visitor: {
            session_id: this.sessionCookie.get("session_id"),
            guest_id: this.sessionCookie.get("guest_id"),
            client_width:
              document.documentElement.clientWidth || window.innerWidth
          }
        };
      });
      KeenClient.recordEvent("pageviews", {});
      window.addEventListener("codeblock-copy", e => {
        KeenClient.recordEvent("codeblock-copy", {
          type: e.codeType
        });
      });
    }
    window.addEventListener("playground-interacted", e => {
       if(!this.shouldTrack){
          return;
        }
      if (this.hasPlaygroundBeenTracked) {
        return;
      }
      this.hasPlaygroundBeenTracked = true;
      KeenClient.recordEvent("playground-interacted", {
        type: e.codeType
      });
    });
    window.addEventListener("keydown", function(e){
      if(e.ctrlKey && e.keyCode===90){
        this.popState();
      }
    }.bind(this));
  }
};
</script>

<style>

#main-container{
  display: block;
  max-width: 1700px;
  padding: 1em;
}

@media (min-width: 1700px) {

  #main-container {
    margin: 0 calc((100% - 1700px) / 2);
  }
}

#app h2{
  padding-bottom: 6px;
}

#app .sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

#app .cta {
    background: #28d86c;
    color: #fff;
    text-decoration: none;
    border-radius: 3px;
    box-shadow: 2px 2px 4px 0 rgba(0, 0, 0, .12);
    font-weight: 500;
    padding: 3px 2em;
    line-height: 2.25em;
    top: -20px;
    transition: all .3s ease-in-out;
    font-size: 1.5em;
    border: none;
}

#app .cta:hover {
  background: #313648;
  color: #fff;
  cursor: pointer;
}

#app .installation-toggle-wrapper{
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

#app .installation-toggle{
    font-size: 2em;
    line-height: 2.25em;
    margin-top: 1.5em;
}

#app .separator {
  margin-bottom: 2.5em;
}

#app #main-container h2,
#app .preset-wrapper {
  width: 100%;
  border-bottom: 3px solid #4a4f62;
  margin: 0.83em 0;
}

#app .row {
  display: flex;
  flex-direction: row;
  width: 100%;
}

#app .row.stretch {
  width: 100%;
  justify-content: space-between;
  align-items: center;
}

h2#configuration,
h2#playground-lg,
#h2#playground-sm,
h2#installation {
  position: relative;
}

#app .h2-like-separator {
  display: none;
}

@media (max-width: 1700px) {
  h2#playground-lg {
    display: none;
  }
}

@media (min-width: 1700px) {
  h2#playground-sm {
    display: none;
  }
}

#app .main-wrapper .config-wrapper {
  position: relative;
}

#app .main-wrapper .config-wrapper .config-group.presets {
  position: absolute;
  top: 0em;
  right: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  width: auto;
}

#app .main-wrapper .config-wrapper .config-group.presets .radio-group {
  width: auto;
  margin-left: 1em;
}

#app .main-wrapper {
  display: flex;
  flex-direction: column-reverse;
}

#app .main-wrapper .config-wrapper {
  flex-direction: column;
}

#app h4{
  font-weight: 500;
}

/* Main View Styles */
@media (min-width: 1700px) {
 #app .h2-like-separator {
    display: block;
    position: relative;
    height: 61px;
    width: 100%;
    margin: 1.33333em 0;
    border-bottom: 3px solid #4a4f62;
  }

  h2#configuration {
    display: none;
  }

  #app .main-wrapper {
    display: flex;
    flex-direction: row;
  }

  #app .main-wrapper section#preview {
    width: 65%;
    min-width: 1052px;
  }

  #app .main-wrapper .config-wrapper {
    flex-direction: column;
  }

  #app .main-wrapper .config-wrapper h2 {
    color: transparent;
    clip: rect(0, 0, 0, 0);
  }

  #app .main-wrapper .config-wrapper .config-group,
  #app .main-wrapper .config-wrapper .config-group > .row,
  #app .main-wrapper
    .config-wrapper
    .config-group.suggests
    > .column
    > .row:last-of-type {
    flex-direction: column;
  }

  nav#config-groups {
    border-bottom: 1px solid #4a4f62;
    padding-bottom: 8px;
    margin-top: 7px;
  }

  #app .main-wrapper .config-wrapper {
    padding-left: 2em;
  }

  #preview-groups .nav-post {
    margin-right: 1em;
  }
}
#app input[type="text"], #app input[type="number"], #app select {
    border-radius: 3px;
    border: 1px solid #dedede;
    font-size: 16px;
    line-height: 28px;
    color: #4a4f62;
    appearance: none;
    box-shadow: 2px 2px 4px 0 rgba(0,0,0,0.12);
}

#app pre {
	letter-spacing: initial;
}
</style>



// WEBPACK FOOTER //
// src/App.vue